// _productsNavToggleSp.ts
import { SingleProductsNavSp } from './_projuctsNavSp';

class SingleProductsNavToggle {
  private link: HTMLAnchorElement;
  private parentNav: HTMLElement | null;
  private navContent: HTMLElement | null;
  private isTransitioning: boolean = false;
  private readonly TRANSITION_DURATION = 150;
  private navFocusManager: SingleProductsNavSp | null = null;
  private originalText: string = ''; // 元のテキストを保存

  constructor(link: HTMLAnchorElement, navFocusManager?: SingleProductsNavSp) {
    this.link = link;
    this.parentNav = link.closest('.ist-productsTop__nav');
    this.navContent =
      this.parentNav?.querySelector('.ist-productsTop__navContent') ?? null;
    if (navFocusManager) {
      this.navFocusManager = navFocusManager;
    }
    this.setup();
  }

  public setNavFocusManager(manager: SingleProductsNavSp) {
    this.navFocusManager = manager;
  }

  private setup() {
    if (!this.parentNav || !this.navContent) return;

    const navContentId = `nav-content-${Math.random().toString(36).substr(2, 9)}`;
    this.navContent.id = navContentId;

    this.link.setAttribute('aria-expanded', 'false');
    this.link.setAttribute('aria-controls', navContentId);
    this.navContent.setAttribute('aria-hidden', 'true');

    // 元のテキストを保存
    this.originalText = this.link.textContent?.trim() || '';

    this.attachEvents();
  }

  /**
   * テキスト内の「開く」を「閉じる」に、「閉じる」を「開く」に変更
   */
  private updateText(isOpen: boolean): void {
    if (!this.originalText) return;

    let newText = this.originalText;

    if (isOpen) {
      // 開く状態の場合：「開く」を「閉じる」に変更
      newText = newText.replace(/開く/g, '閉じる');
    } else {
      // 閉じる状態の場合：「閉じる」を「開く」に変更
      newText = newText.replace(/閉じる/g, '開く');
    }

    this.link.textContent = newText;
  }

  private documentKeydownHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.isExpanded()) {
      this.closeNav();
    }
  };

  private documentClickHandler = (e: MouseEvent) => {
    if (this.isExpanded() && !this.parentNav?.contains(e.target as Node)) {
      this.closeNav();
    }
  };

  private attachEvents() {
    this.link.addEventListener('click', (e) => {
      e.preventDefault();
      if (!this.isTransitioning) {
        this.toggleNav();
      }
    });

    document.addEventListener('keydown', this.documentKeydownHandler);
    document.addEventListener('click', this.documentClickHandler);
  }

  private toggleNav() {
    this.isExpanded() ? this.closeNav() : this.openNav();
  }

  private openNav() {
    if (!this.parentNav || !this.navContent || this.isTransitioning) return;

    this.isTransitioning = true;
    this.parentNav.classList.add('ist-productsTop__nav--open');
    this.link.setAttribute('aria-expanded', 'true');
    this.navContent.setAttribute('aria-hidden', 'false');

    // テキストを「閉じる」に変更
    this.updateText(true);

    const actionContent = document.querySelector(
      '.ist-js__projuctsNavItemActionContent'
    );
    if (actionContent) {
      actionContent.classList.add('ist-js__projuctsNavItemActionContent--open');
    }

    requestAnimationFrame(() => {
      setTimeout(() => {
        this.navContent?.classList.add('ist-productsTop__navContent--open');

        // navを開くときにフォーカスマネージャを有効化
        this.navFocusManager?.activate();

        setTimeout(() => {
          this.isTransitioning = false;
        }, this.TRANSITION_DURATION);
      }, 0);
    });
  }

  private closeNav() {
    if (!this.parentNav || !this.navContent || this.isTransitioning) return;

    this.isTransitioning = true;
    this.navContent.classList.remove('ist-productsTop__navContent--open');
    this.link.setAttribute('aria-expanded', 'false');

    // テキストを「開く」に変更
    this.updateText(false);

    const actionContent = document.querySelector(
      '.ist-js__projuctsNavItemActionContent'
    );
    if (actionContent) {
      actionContent.classList.remove(
        'ist-js__projuctsNavItemActionContent--open'
      );
    }

    setTimeout(() => {
      this.parentNav?.classList.remove('ist-productsTop__nav--open');
      this.navContent?.setAttribute('aria-hidden', 'true');
      this.isTransitioning = false;

      // navを閉じるときにフォーカスマネージャを無効化
      this.navFocusManager?.deactivate();
    }, this.TRANSITION_DURATION);
  }

  private isExpanded(): boolean {
    return this.link.getAttribute('aria-expanded') === 'true';
  }

  public dispose() {
    document.removeEventListener('keydown', this.documentKeydownHandler);
    document.removeEventListener('click', this.documentClickHandler);
  }
}

export default class ProductsNavToggleSp {
  private navLinks: HTMLAnchorElement[];
  private navToggles: SingleProductsNavToggle[] = [];
  private navFocusManagers: SingleProductsNavSp[] = [];

  constructor() {
    this.navLinks = Array.from(
      document.querySelectorAll('a.ist-productsTop__navTitle')
    ) as HTMLAnchorElement[];
    this.init();
  }

  private init() {
    // 初期段階ではまだフォーカスマネージャ未設定
    this.navToggles = this.navLinks.map(
      (link) => new SingleProductsNavToggle(link)
    );
  }

  // 外部で生成した ProductsNavSp から SingleProductsNavSp の配列を受け取り、関連付ける
  public setNavFocusManagers(managers: SingleProductsNavSp[]) {
    this.navFocusManagers = managers;
    // navLinks と navFocusManagers の対応付けロジックは任意（ここでは数が同じと想定し、1:1対応）
    this.navToggles.forEach((toggle, i) => {
      const manager = this.navFocusManagers[i];
      if (manager) {
        toggle.setNavFocusManager(manager);
      }
    });
  }

  public dispose() {
    this.navToggles.forEach((toggle) => toggle.dispose());
    this.navToggles = [];
  }
}
